import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import {
  createUserMatchingDeck,
  updateUserMatchingDeck,
  getUserMatchingDeckById,
  uploadMatchingCardImage,
} from '../services/firestoreService';
import { DECKS } from '../data/stimuli';
import { CARD_LIBRARY } from '../data/cardLibrary';
import { sanitizeInput, CHAR_LIMITS } from '../utils/sanitize';
import { validateImage } from '../utils/validateImage';
import { FiPlus, FiTrash2, FiChevronUp, FiChevronDown, FiUpload, FiCamera, FiSearch, FiGrid } from 'react-icons/fi';
import '../styles/deck-builder.css';
import '../styles/sequencing-builder.css';

const PEXELS_KEY = process.env.REACT_APP_PEXELS_API_KEY;

// Every card in every built-in deck, every one of its photo variations, and
// the loose card library — lets someone build a matching activity entirely
// from existing stimuli instead of only ever uploading brand new photos.
const LIBRARY_ITEMS = [
  ...DECKS.flatMap(deck => deck.items.flatMap(item => [
    ...(item.image ? [{ label: item.label, image: item.image, category: deck.name }] : []),
    ...(item.variations || []).map((url, i) => ({
      label: `${item.label} (alt ${i + 1})`, image: url, category: deck.name,
    })),
  ])),
  ...CARD_LIBRARY.map(c => ({ label: c.label, image: c.image, category: c.category })),
].filter(item => item.image);

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Mirrors buildTrialsFromCards() in MatchingGenerateFromDeckPage.js
function buildTrialsFromCards(cards, { choiceCount, nonIdentical }) {
  const effChoiceCount = Math.min(choiceCount, cards.length);
  if (effChoiceCount < 2) return [];

  return cards.map((card, idx) => {
    const distractors = shuffle(cards.filter((_, i) => i !== idx)).slice(0, effChoiceCount - 1);
    const media = [...new Set([card.imageUrl].filter(Boolean))];

    return {
      id: `t_${Date.now().toString(36)}_${idx}`,
      order: idx + 1,
      prompt: { label: card.label, imageUrl: card.imageUrl, media },
      choices: [
        { id: `c_${card.uid}`, label: card.label, imageUrl: card.imageUrl, isCorrect: true },
        ...distractors.map(d => ({ id: `c_${d.uid}`, label: d.label, imageUrl: d.imageUrl, isCorrect: false })),
      ],
    };
  });
}

let cardUid = 0;
const newCard = () => ({
  uid: `card_${Date.now()}_${cardUid++}`,
  label: '',
  imageUrl: null,
  imageFile: null,
  imageSource: null, // 'web' | 'uploaded' | 'library'
});

// ── Per-card image picker: search the web, upload, use the camera, or pick
// something already in the app's card library ───────────────────────────────

const CardImagePicker = ({ card, onChange }) => {
  const [mode, setMode] = useState(null); // null | 'search' | 'library'
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const runSearch = async (term) => {
    const q = (term ?? query ?? card.label).trim();
    if (!q) return;
    setSearching(true);
    setResults([]);
    try {
      const res = await fetch(
        `https://api.pexels.com/v1/search?query=${encodeURIComponent(q)}&per_page=12&orientation=square`,
        { headers: { Authorization: PEXELS_KEY } }
      );
      const data = await res.json();
      setResults(data.photos || []);
    } catch {
      setResults([]);
    }
    setSearching(false);
  };

  const pickPhoto = (photo) => {
    onChange({ ...card, imageUrl: photo.src.medium, imageFile: null, imageSource: 'web' });
    setResults([]);
    setMode(null);
  };

  const pickLibraryItem = (item) => {
    onChange({
      ...card,
      imageUrl: item.image,
      imageFile: null,
      imageSource: 'library',
      label: card.label || item.label,
    });
    setMode(null);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setError('');
    const result = await validateImage(file);
    if (!result.valid) { setError(result.error); return; }
    if (card.imageSource === 'uploaded' && card.imageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(card.imageUrl);
    }
    onChange({ ...card, imageUrl: URL.createObjectURL(file), imageFile: file, imageSource: 'uploaded' });
  };

  const removeImage = () => {
    if (card.imageSource === 'uploaded' && card.imageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(card.imageUrl);
    }
    onChange({ ...card, imageUrl: null, imageFile: null, imageSource: null });
  };

  const filteredLibrary = (librarySearch.trim()
    ? LIBRARY_ITEMS.filter(item =>
        item.label.toLowerCase().includes(librarySearch.toLowerCase()) ||
        item.category.toLowerCase().includes(librarySearch.toLowerCase()))
    : LIBRARY_ITEMS
  ).slice(0, 48);

  if (card.imageUrl) {
    return (
      <div className="seqb-step-preview">
        <img src={card.imageUrl} alt="" className="seqb-step-preview-img" />
        <button type="button" className="seqb-step-preview-remove" onClick={removeImage}>
          ✕ Remove photo
        </button>
      </div>
    );
  }

  return (
    <div className="seqb-img-picker">
      <div className="db-img-source-row">
        <button
          type="button"
          className={`db-img-source-btn${mode === 'search' ? ' active' : ''}`}
          onClick={() => { setMode('search'); if (results.length === 0) runSearch(card.label); }}
        >
          <FiSearch size={13} /> Search Web
        </button>
        <button type="button" className="db-img-source-btn" onClick={() => fileInputRef.current?.click()}>
          <FiUpload size={13} /> Upload
        </button>
        <button type="button" className="db-img-source-btn" onClick={() => cameraInputRef.current?.click()}>
          <FiCamera size={13} /> Camera
        </button>
        <button
          type="button"
          className={`db-img-source-btn${mode === 'library' ? ' active' : ''}`}
          onClick={() => setMode(m => m === 'library' ? null : 'library')}
        >
          <FiGrid size={13} /> My Library
        </button>
      </div>

      {mode === 'search' && (
        <div className="db-search-panel">
          <form className="db-search-input-row" onSubmit={e => { e.preventDefault(); runSearch(query); }}>
            <input
              className="seqb-input"
              type="text"
              placeholder={card.label.trim() ? `Search "${card.label.trim()}"…` : 'Type a search term'}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <button className="db-search-go-btn" type="submit" disabled={searching || (!query.trim() && !card.label.trim())}>
              {searching ? '…' : 'Search'}
            </button>
          </form>

          {results.length > 0 && (
            <div className="db-search-results-grid" style={{ marginTop: 8 }}>
              {results.map(photo => (
                <div key={photo.id} className="db-search-result-tile" onClick={() => pickPhoto(photo)}>
                  <img
                    src={photo.src?.small}
                    alt=""
                    className="db-search-result-img"
                    onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
                  />
                </div>
              ))}
            </div>
          )}

          {!searching && results.length === 0 && (query || card.label) && (
            <p className="seqb-hint" style={{ marginTop: 8 }}>No results — try a different keyword, or upload/take a photo instead.</p>
          )}
        </div>
      )}

      {mode === 'library' && (
        <div className="db-search-panel">
          <input
            className="seqb-input"
            type="text"
            placeholder="Search your card library…"
            value={librarySearch}
            onChange={e => setLibrarySearch(e.target.value)}
          />
          <div className="db-search-results-grid" style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto' }}>
            {filteredLibrary.map((item, i) => (
              <div key={item.label + i} className="db-search-result-tile" onClick={() => pickLibraryItem(item)} title={item.label}>
                <img src={item.image} alt={item.label} className="db-search-result-img" />
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <p className="db-upload-error">{error}</p>}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Matching Builder — build your own matching activity from your own photos or
// anything already in the app's card library. Private to you; no admin review.
// ══════════════════════════════════════════════════════════════════════════════

export const MatchingBuilderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const editDeckId = location.state?.editDeckId ?? null;

  const [title, setTitle] = useState('');
  const [cards, setCards] = useState([newCard(), newCard()]);
  const [nonIdentical, setNonIdentical] = useState(true);
  const [choiceCount, setChoiceCount] = useState(3);

  const [editLoading, setEditLoading] = useState(!!editDeckId);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!editDeckId) return;
    getUserMatchingDeckById(editDeckId).then(result => {
      if (result.success) {
        const d = result.deck;
        setTitle(d.title || '');
        setNonIdentical(d.nonIdentical ?? true);
        setChoiceCount(d.trials?.[0]?.choices?.length || 3);
        setCards(
          (d.trials || []).length
            ? d.trials.map(t => ({
                uid: t.id || newCard().uid,
                label: t.prompt.label || '',
                imageUrl: t.prompt.imageUrl || null,
                imageFile: null,
                imageSource: t.prompt.imageUrl ? 'library' : null,
              }))
            : [newCard(), newCard()]
        );
      }
      setEditLoading(false);
    });
  }, [editDeckId]);

  const updateCard = (uid, next) => {
    setCards(prev => prev.map(c => (c.uid === uid ? next : c)));
  };

  const removeCard = (uid) => {
    setCards(prev => prev.filter(c => c.uid !== uid));
  };

  const moveCard = (index, dir) => {
    setCards(prev => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addCard = () => setCards(prev => [...prev, newCard()]);

  const canSave = title.trim().length > 0
    && cards.length >= 2
    && cards.every(c => c.label.trim().length > 0 && c.imageUrl);

  const handleSave = async () => {
    if (!canSave || saving || !currentUser) return;
    setSaving(true);
    setSaveError('');

    try {
      const payload = {
        title: sanitizeInput(title.trim()),
        nonIdentical,
      };

      let deckId = editDeckId;
      if (editDeckId) {
        await updateUserMatchingDeck(editDeckId, payload);
      } else {
        const createRes = await createUserMatchingDeck(currentUser.uid, payload);
        if (!createRes.success) throw new Error(createRes.error || 'Could not create activity.');
        deckId = createRes.id;
      }

      const finalCards = [];
      for (const card of cards) {
        let imageUrl = card.imageUrl;
        if (card.imageFile) {
          const uploadRes = await uploadMatchingCardImage(currentUser.uid, deckId, card.uid, card.imageFile);
          if (!uploadRes.success) throw new Error(uploadRes.error || 'Image upload failed.');
          imageUrl = uploadRes.url;
        }
        finalCards.push({ uid: card.uid, label: sanitizeInput(card.label.trim()), imageUrl });
      }

      const trials = buildTrialsFromCards(finalCards, { choiceCount, nonIdentical });

      await updateUserMatchingDeck(deckId, { trials, trialCount: trials.length });

      navigate('/matching', { state: { newDeckId: deckId } });
    } catch (err) {
      setSaveError(err.message || 'Something went wrong saving your activity.');
      setSaving(false);
    }
  };

  if (editLoading) {
    return (
      <Layout>
        <div className="seqb-page">
          <div className="seqb-loading">Loading your activity…</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="seqb-page">
        <div className="page-header">
          <h1 className="deck-page-title">{editDeckId ? 'Edit Matching Activity' : 'Create Matching Activity'}</h1>
          <p>Build your own matching game using your own photos, the camera, or anything in your card library.</p>
        </div>

        <div className="seqb-card">
          <div className="seqb-field">
            <label className="seqb-field-label">Activity Name *</label>
            <input
              className="db-input db-input-large"
              placeholder="e.g., My Favorite Animals"
              value={title}
              onChange={e => { if (e.target.value.length <= CHAR_LIMITS.deckTitle) setTitle(e.target.value); }}
              maxLength={CHAR_LIMITS.deckTitle}
            />
          </div>

          <div className="seqb-field">
            <label className="seqb-field-label">Matching Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setNonIdentical(false)}
                style={{
                  flex: 1, padding: '12px 14px', borderRadius: 12, border: '1.5px solid',
                  borderColor: !nonIdentical ? '#8B2635' : '#E5E7EB',
                  background: !nonIdentical ? '#8B263518' : '#F9FAFB',
                  color: !nonIdentical ? '#8B2635' : '#6B7280',
                  fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700 }}>Identical</div>
                <div style={{ fontSize: 11, marginTop: 2 }}>Match the exact same photo.</div>
              </button>
              <button
                type="button"
                onClick={() => setNonIdentical(true)}
                style={{
                  flex: 1, padding: '12px 14px', borderRadius: 12, border: '1.5px solid',
                  borderColor: nonIdentical ? '#8B2635' : '#E5E7EB',
                  background: nonIdentical ? '#8B263518' : '#F9FAFB',
                  color: nonIdentical ? '#8B2635' : '#6B7280',
                  fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700 }}>Non-Identical</div>
                <div style={{ fontSize: 11, marginTop: 2 }}>Match a different photo of the same item.</div>
              </button>
            </div>
          </div>

          <div className="seqb-field">
            <label className="seqb-field-label">Cards to Display per Trial</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[2, 3, 4].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setChoiceCount(n)}
                  style={{
                    padding: '7px 16px', borderRadius: 20, border: '1.5px solid',
                    borderColor: choiceCount === n ? '#8B2635' : '#E5E7EB',
                    background:  choiceCount === n ? '#8B263518' : '#F9FAFB',
                    color:       choiceCount === n ? '#8B2635' : '#6B7280',
                    fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="seqb-card">
          <div className="seqb-card-header">
            <h2 className="seqb-card-title">Cards</h2>
            <span className="seqb-step-count">{cards.length} card{cards.length !== 1 ? 's' : ''}</span>
          </div>

          {cards.map((card, i) => (
            <div key={card.uid} className="seqb-step-row">
              <div className="seqb-step-order-col">
                <span className="seqb-step-order-badge">{i + 1}</span>
                <button type="button" className="seqb-step-move-btn" disabled={i === 0} onClick={() => moveCard(i, -1)} title="Move up">
                  <FiChevronUp size={14} />
                </button>
                <button type="button" className="seqb-step-move-btn" disabled={i === cards.length - 1} onClick={() => moveCard(i, 1)} title="Move down">
                  <FiChevronDown size={14} />
                </button>
              </div>

              <div className="seqb-step-body">
                <div className="seqb-field">
                  <label className="seqb-field-label">Card {i + 1} label *</label>
                  <input
                    className="db-input"
                    placeholder="e.g., My dog Max"
                    value={card.label}
                    onChange={e => {
                      if (e.target.value.length <= CHAR_LIMITS.cardLabel) {
                        updateCard(card.uid, { ...card, label: e.target.value });
                      }
                    }}
                    maxLength={CHAR_LIMITS.cardLabel}
                  />
                </div>

                <CardImagePicker card={card} onChange={next => updateCard(card.uid, next)} />
              </div>

              <button
                type="button"
                className="seqb-step-remove-btn"
                onClick={() => removeCard(card.uid)}
                disabled={cards.length <= 2}
                title="Remove card"
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          ))}

          <button type="button" className="seqb-add-step-btn" onClick={addCard}>
            <FiPlus size={15} /> Add Card
          </button>
        </div>

        {saveError && <p className="db-upload-error">{saveError}</p>}

        <div className="seqb-save-row">
          <button
            type="button"
            className="seqb-save-btn"
            disabled={!canSave || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : editDeckId ? 'Save Changes' : 'Save Activity'}
          </button>
          {!canSave && (
            <span className="seqb-hint">Give it a name, and at least 2 cards each with a label and a photo.</span>
          )}
        </div>
      </div>
    </Layout>
  );
};
